import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { CustomDivider } from "@/components/CustomDivider";
import { ArrowIcon, Button as Button95, Checkbox, Hourglass, NumberInput, Panel, Slider, Text, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller, Resolver } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useRouter } from 'expo-router';
import { AppText } from '@/components/AppText';
import { RangeSlider } from '@react-native-assets/slider';

const styles = StyleSheet.create({
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  container: {
    gap: 20,
    alignItems: "center",
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
    alignItems: "center",
  },
  numeric: {
    width: 80,
  },
});

interface MatchesFilterProps {
  onSubmit: (ageMin : number, ageMax : number, heightMin : number, heightMax : number, maxDistance : number) => Promise<void>,
  showDistance?: boolean,
  baselineAgeMin: number,
  baselineAgeMax: number,
  baselineHeightMin: number,
  baselineHeightMax: number,
  baselineMaxDistance: number,
};

export function MatchesFilter({
  onSubmit,
  showDistance = true,
  baselineAgeMin,
  baselineAgeMax,
  baselineHeightMin,
  baselineHeightMax,
  baselineMaxDistance
} : MatchesFilterProps) {
  const [processing, setProcessing] = useState(false);

  const [ageValues, setAgeValues] = useState<[number, number]>([
    baselineAgeMin,
    baselineAgeMax,
  ]);
  const [ageMin, setAgeMin] = useState<number>(
    baselineAgeMin
  );
  const [ageMax, setAgeMax] = useState<number>(
    baselineAgeMax
  );

  const [heightValues, setHeightValues] = useState<[number, number]>([
    baselineHeightMin,
    baselineHeightMax,
  ]);
  const [heightMin, setHeightMin] = useState(
    baselineHeightMin
  );
  const [heightMax, setHeightMax] = useState(
    baselineHeightMax
  );

  const [maxDistance, setMaxDistance] = useState<string>(baselineMaxDistance <= 0 ? "" : "" + baselineMaxDistance);

  const handleTemporaryAgeValuesChange = (newValues: number | [number, number]) => {
    const rangeValues = newValues as [number, number];
    setAgeValues([Math.trunc(rangeValues[0]), Math.trunc(rangeValues[1])]);
  };

  const handleFinalAgeValuesChange = (newValues: number | [number, number]) => {
    const rangeValues = newValues as [number, number];
    console.log("handleFinalAgeValuesChange: " + rangeValues[0] + ", " + rangeValues[1]);
    setAgeMin(Math.trunc(rangeValues[0]));
    setAgeMax(Math.trunc(rangeValues[1]));
  };

  const handleTemporaryHeightValuesChange = (newValues: number | [number, number]) => {
    const rangeValues = newValues as [number, number];
    setHeightValues([Math.trunc(rangeValues[0]), Math.trunc(rangeValues[1])]);
  };

  const handleFinalHeightValuesChange = (newValues: number | [number, number]) => {
    const rangeValues = newValues as [number, number];
    setHeightMin(Math.trunc(rangeValues[0]));
    setHeightMax(Math.trunc(rangeValues[1]));
  };

  const onInternalSubmit = useCallback(async () => {
    setProcessing(true);
    try {
      await onSubmit(ageMin, ageMax, heightMin, heightMax, maxDistance.length == 0 ? 0 : parseInt(maxDistance));
    } finally {
      setProcessing(false);
    }
  }, [onSubmit, ageMin, ageMax, heightMin, heightMax, maxDistance]);

  useEffect(() => {
    if (baselineAgeMin >= AppConsts.MIN_AGE && baselineAgeMin <= AppConsts.MAX_AGE &&
        baselineAgeMax >= AppConsts.MIN_AGE && baselineAgeMax <= AppConsts.MAX_AGE
    ) {
      handleTemporaryAgeValuesChange([baselineAgeMin, baselineAgeMax]);
      handleFinalAgeValuesChange([baselineAgeMin, baselineAgeMax]);
    }
  }, [baselineAgeMin, baselineAgeMax]);

  useEffect(() => {
    if (baselineHeightMin >= AppConsts.MIN_HEIGHT_INCHES && baselineHeightMin <= AppConsts.MAX_HEIGHT_INCHES &&
        baselineHeightMax >= AppConsts.MIN_HEIGHT_INCHES && baselineHeightMax <= AppConsts.MAX_HEIGHT_INCHES
    ) {
      handleTemporaryHeightValuesChange([baselineHeightMin, baselineHeightMax]);
      handleFinalHeightValuesChange([baselineHeightMin, baselineHeightMax]);
    }
  }, [baselineHeightMin, baselineHeightMax]);

  useEffect(() => {
    setMaxDistance(baselineMaxDistance <= 0 ? "" : "" + baselineMaxDistance);
  }, [baselineMaxDistance]);

  const thumb = useCallback(AppUtils.RangeSliderThumb, []);

  return (
    <View style={styles.container}>

      <CustomText>Desired Ages</CustomText>

      <AppText style={AppConsts.STYLES.fontMono}>{ageValues[0]} - {ageValues[1]}</AppText>

      {
        // https://github.com/Sharcoux/slider#range-slider
      }
      <RangeSlider
        minimumValue={AppConsts.MIN_AGE}
        maximumValue={AppConsts.MAX_AGE}
        range={ageValues}
        step={1}
        onValueChange={handleTemporaryAgeValuesChange}
        onSlidingComplete={handleFinalAgeValuesChange}
        style={AppConsts.STYLES.rangeSlider}
        CustomThumb={thumb}
        outboundColor={AppConsts.COLOR3}
        inboundColor={AppConsts.COLOR1}
      />

      <CustomDivider fullWidth={true} />

      <CustomText>Desired Height</CustomText>

      <AppText style={AppConsts.STYLES.fontMono}>{AppUtils.getHeightHumanReadable(heightValues[0], true)} - {AppUtils.getHeightHumanReadable(heightValues[1], true)}</AppText>

      {
        // https://github.com/Sharcoux/slider#range-slider
      }
      <RangeSlider
        minimumValue={AppConsts.MIN_HEIGHT_INCHES}
        maximumValue={AppConsts.MAX_HEIGHT_INCHES}
        range={heightValues}
        step={1}
        onValueChange={handleTemporaryHeightValuesChange}
        onSlidingComplete={handleFinalHeightValuesChange}
        style={AppConsts.STYLES.rangeSlider}
        CustomThumb={thumb}
        outboundColor={AppConsts.COLOR3}
        inboundColor={AppConsts.COLOR1}
      />

      { showDistance && (
          <>
            <CustomDivider fullWidth={true} />

            <CustomText>Max Distance</CustomText>

            <View style={styles.numericWrapper}>
              <Button95
                disabled={maxDistance.length == 0 || parseInt(maxDistance) == 0}
                onPress={() => setMaxDistance("" + (parseInt(maxDistance) - 1))}
              >
                <ArrowIcon
                  segments={4}
                  disabled={maxDistance.length == 0 || parseInt(maxDistance) == 0}
                  direction='left'
                />
              </Button95>
              <TextInput
                value={maxDistance.length == 0 || parseInt(maxDistance) == 0 ? "" : "" + maxDistance}
                onChangeText={(v) => {
                  if (v.length > 0 && v != "NaN") {
                    setMaxDistance("" + parseInt(v))
                  } else {
                    setMaxDistance("");
                  }
                }}
                inputMode='numeric'
                keyboardType='number-pad'
                placeholder=''
                textAlign='center'
                style={styles.numeric}
              />
              <AppText style={{ fontSize: 15, alignSelf: "center" }}>miles</AppText>
              <Button95
                disabled={parseInt(maxDistance) == AppConsts.MAXIMUM_FILTER_MAX_DISTANCE_MILES}
                onPress={() => setMaxDistance(maxDistance.length == 0 ? "1" : "" + (parseInt(maxDistance) + 1))}
              >
                <ArrowIcon
                  segments={4}
                  disabled={parseInt(maxDistance) == AppConsts.MAXIMUM_FILTER_MAX_DISTANCE_MILES}
                  direction='right'
                />
              </Button95>
            </View>
          </>
        )
      }

      <CustomDivider fullWidth={true} />

      <View style={styles.buttonWrapper}>
        <Button95 disabled={processing} onPress={onInternalSubmit}>Apply</Button95>
      </View>
    </View>
  );
}
